import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import type { ApiError } from '@/lib/api';

export function LoginPage() {
  const { t } = useTranslation();
  const { user, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) return setError(t('login.enterEmail'));
    if (!password) return setError(t('login.enterPassword'));
    setBusy(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      const code = (err as ApiError).code;
      setError(
        code === 'EMAIL_NOT_VERIFIED' ? t('login.emailNotVerified') : t('login.invalidCredentials')
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-stone-900 p-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl sm:p-8">
        <h1 className="text-2xl font-semibold text-stone-900">{t('login.title')}</h1>
        <p className="mt-1 text-sm text-stone-500">{t('login.subtitle')}</p>
        <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
          <label className="text-sm">
            <span className="mb-1 block font-medium text-stone-700">{t('login.emailLabel')}</span>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('login.emailPlaceholder')}
              className="w-full rounded-md border border-stone-300 px-3 py-2 focus:border-amber-600 focus:outline-none"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium text-stone-700">
              {t('login.passwordLabel')}
            </span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('login.passwordPlaceholder')}
              className="w-full rounded-md border border-stone-300 px-3 py-2 focus:border-amber-600 focus:outline-none"
            />
          </label>
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            className="rounded-md bg-amber-700 px-4 py-2.5 font-medium text-white transition-colors hover:bg-amber-800 disabled:opacity-60"
          >
            {busy ? t('login.entering') : t('login.enter')}
          </button>
        </form>
        <p className="mt-6 text-center text-xs text-stone-400">
          v{import.meta.env.VITE_APP_VERSION ?? 'dev'}
        </p>
      </div>
    </div>
  );
}
